import { useState, useEffect } from "react";
import { useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, ShieldCheck, AlertTriangle, FileText } from "lucide-react";

interface SignOffRequestData {
  request: {
    id: number;
    supervisorName: string;
    supervisorRole: string;
    nurseName: string;
    competencyCode: string;
    competencyStatement: string;
    outcome: "pending" | "confirmed" | "declined";
    expiresAt: string;
    createdAt: string;
  };
  entry: {
    reflectionText: string | null;
    assessorNotes: string | null;
    practiceLevel: string;
    status: string;
    achievedAt: string | null;
  } | null;
  evidenceCount: number;
}

export default function SignOffConfirm() {
  const params = useParams<{ token: string }>();
  const token = params.token;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SignOffRequestData | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<"confirmed" | "declined" | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const res = await fetch(`/api/sign-off/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string };
          setFetchError(body.error ?? "This sign-off link is invalid or has expired.");
          return;
        }
        const json = await res.json() as SignOffRequestData;
        setData(json);
      } catch {
        setFetchError("Failed to load sign-off request. Please check your internet connection.");
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [token]);

  const handleRespond = async (outcome: "confirmed" | "declined") => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`/api/sign-off/${token}/${outcome}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comments: comments.trim() || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? "Failed to submit response");
      }
      setSubmitted(outcome);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  const practiceLabels: Record<string, string> = {
    specialist: "Specialist",
    advanced: "Advanced",
    consultant: "Consultant",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          <p className="text-sm text-slate-500">Loading sign-off request...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 max-w-md w-full text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link unavailable</h1>
          <p className="text-sm text-slate-500 leading-relaxed">{fetchError}</p>
          <p className="text-xs text-slate-400 mt-4">
            If you believe this is an error, please ask the nurse to send a new sign-off request.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { request, entry, evidenceCount } = data;
  const isExpired = new Date(request.expiresAt) < new Date();
  const alreadyResponded = request.outcome !== "pending";

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 max-w-md w-full text-center">
          {submitted === "confirmed" ? (
            <>
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-slate-900 mb-2">Sign-off confirmed</h1>
              <p className="text-sm text-slate-500 leading-relaxed">
                Thank you. Your confirmation has been recorded and {request.nurseName} will be notified.
                The portfolio entry has been updated with your sign-off.
              </p>
            </>
          ) : (
            <>
              <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-slate-900 mb-2">Sign-off declined</h1>
              <p className="text-sm text-slate-500 leading-relaxed">
                Your response has been recorded and {request.nurseName} will be notified with any comments you left.
              </p>
            </>
          )}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <p className="text-xs text-slate-400">RheumHub · UK Rheumatology Platform</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <span className="text-2xl font-bold text-teal-700">RheumHub</span>
          <span className="text-sm text-slate-400">Supervisor Sign-Off</span>
        </div>

        {(isExpired || alreadyResponded) && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800">
              {alreadyResponded
                ? `This sign-off request has already been ${request.outcome}.`
                : "This sign-off link has expired. Please ask the nurse to send a new request."}
            </p>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-teal-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5">Sign-off request</p>
              <h1 className="text-xl font-bold text-slate-900">
                {request.competencyCode}
              </h1>
              <p className="text-sm text-slate-600 leading-relaxed mt-1">{request.competencyStatement}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <p className="text-xs text-slate-400 font-medium mb-0.5">Nurse</p>
              <p className="text-sm font-semibold text-slate-900">{request.nurseName}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium mb-0.5">Requested</p>
              <p className="text-sm font-semibold text-slate-900">
                {new Date(request.createdAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            </div>
            {entry && (
              <div>
                <p className="text-xs text-slate-400 font-medium mb-0.5">Practice level</p>
                <p className="text-sm font-semibold text-slate-900">{practiceLabels[entry.practiceLevel] ?? entry.practiceLevel}</p>
              </div>
            )}
            {evidenceCount > 0 && (
              <div className="flex items-center gap-1.5">
                <FileText className="h-4 w-4 text-slate-400" />
                <p className="text-sm text-slate-600">{evidenceCount} evidence file{evidenceCount !== 1 ? "s" : ""} attached</p>
              </div>
            )}
          </div>
        </div>

        {entry?.reflectionText && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Nurse Reflection</h2>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{entry.reflectionText}</p>
          </div>
        )}

        {entry?.assessorNotes && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Assessor Notes</h2>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{entry.assessorNotes}</p>
          </div>
        )}

        {!alreadyResponded && !isExpired && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
            <h2 className="text-base font-bold text-slate-900">Your response</h2>
            <p className="text-sm text-slate-500">
              As <strong className="text-slate-700">{request.supervisorName}</strong> ({request.supervisorRole}),
              please review the reflection above and confirm or decline sign-off for this competency.
            </p>

            <div>
              <label htmlFor="signoff-comments" className="block text-xs font-medium text-slate-600 mb-1.5">
                Comments <span className="text-slate-400 font-normal">(optional — recommended if declining)</span>
              </label>
              <textarea
                id="signoff-comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="e.g. Competency demonstrated to a good standard. Observed in clinic on 12 May."
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              />
              <p className="text-xs text-slate-400 mt-1">{comments.length}/2000 characters</p>
            </div>

            {submitError && (
              <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{submitError}</p>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => handleRespond("confirmed")}
                disabled={submitting}
                className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Confirm sign-off
              </Button>
              <Button
                onClick={() => handleRespond("declined")}
                disabled={submitting}
                variant="outline"
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4" />
                Decline
              </Button>
            </div>

            <p className="text-xs text-slate-400 leading-relaxed">
              By confirming, your response will be recorded with the current timestamp and your IP address
              as part of the audit trail for NMC revalidation purposes. This link is single-use.
            </p>
          </div>
        )}

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 text-center">
          RheumHub · UK Rheumatology Platform · <a href="mailto:hello@medantixhealth.co.uk" className="underline hover:text-slate-600">hello@medantixhealth.co.uk</a>
        </div>
      </div>
    </div>
  );
}
